/**
 * Packaging Verification Types
 *
 * Defines types for multi-drug packaging verification workflow.
 * Similar to clinical and product verification but focused on packaging process.
 */

import type { PmsGateResult } from './pmsGate';

export interface PackagingMedicationData {
  rxNumber: string;
  medicationName: string;
  din: string;
  upc: string;
  allUpcs?: string[]; // All valid UPCs from Kroll (for matching any valid UPC)
  drugId: string;
  strength: string;
  quantity: number;
  packCount: number;
  packSize?: number; // GP-475: per-pack size from Kroll (COALESCE(DrgPack.PackSize, Rx.PackSize)); surfaced so the technician can compare against the physical product / Kroll when they differ
  daysSupply?: number; // GP-891: Rx days supply — used to block dispensing product that expires before the supply runs out
  labels?: number; // Rx.Labels from Kroll — number of vial labels configured for this Rx
  status?: number; // Rx.Status code from Kroll (1-8)
  rxAdjState?: number | null; // Kroll Rx.AdjState (0/1/5/6/7 = non-clean adjudication; see RxAdjStateBadge)
  location?: string; // Shelf location from Kroll Drg.Location
  onHandQty?: number; // On-hand inventory from Kroll DrgPack.OnHandQty
  drugNotes?: string; // Drug description from Kroll Drg.Description (physical appearance)
  drugComment?: string; // Drug comment from Kroll Drg.Comment
  /** PRX-59: the backend prescription-status verdict. Null when never computed. */
  pmsGate?: PmsGateResult | null;
  /** PRX-18: the pharmacy system's per-drug label split rule. Null when none. */
  labelSplit?: LabelSplit | null;
}

/**
 * PRX-18: per-drug label split rule, stamped on billingData.medications[] by the
 * Fillware resync (DrugPack.SplitType/SplitQty). Mirrors the backend
 * `LabelSplit` in common/fillware/label-split.util.ts.
 */
export interface LabelSplit {
  source: 'fillware';
  /** qty_per_label: ruleQty is the max units per label. label_count: ruleQty is the number of labels. */
  mode: 'qty_per_label' | 'label_count';
  ruleQty: number;
  /**
   * The quantity split across the labels. Absent on a split stamped before it
   * was recorded; perLabelQty × (labelCount − 1) + lastLabelQty then gives it.
   */
  quantity?: number;
  labelCount: number;
  /** First label's share (qty_per_label: labels 1..N-1). */
  perLabelQty: number;
  /** Last label's share. */
  lastLabelQty: number;
}

/** PRX-18: sent with the packaging item when the printed count differs from the rule. */
export interface LabelSplitOverride {
  mode: LabelSplit['mode'];
  ruleQty: number;
  ruleLabelCount: number;
  labelsPrinted: number;
}

export interface PackagingScan {
  barcode: string;
  lotNumber: string;
  expiryDate: string;
  timestamp: string;
  confirmed?: boolean;
  /** GP-384: Quantity from this stock bottle (part-packaging) */
  quantity?: number;
}

/**
 * Represents a single mixture ingredient's scan record (011-mixture-ingredient-scanning).
 * Each ingredient in a compound mixture gets its own UPC scan with lot/expiry capture.
 */
export interface MixtureIngredientScan {
  din: string;
  drugName: string;
  activeIngredient: string;
  quantity: number;
  unit: string;
  upc: string;
  expectedUpc: string;
  lotNumber: string;
  expiryDate: string;
  scanned: boolean;
  confirmed: boolean;
  scannedAt?: string;
  barcodeType?: string;
  autoExtracted?: boolean;
  rawBarcode?: string;
}

export interface PackagingDrugVerification {
  // Medication identification
  rxNumber: string;
  medicationName: string;
  din: string;
  upc: string;
  strength: string;
  quantity: number;
  packCount: number;

  // Partial fill (balance owing)
  quantityFilled?: number;    // Editable quantity (defaults to `quantity`)
  isPartialFill?: boolean;    // Auto-detected when quantityFilled < quantity
  partialFillReason?: string; // Per-drug reason for partial fill
  partialFillNotes?: string;  // Per-drug notes for partial fill

  // Barcode scans
  rxLabelBarcode: string;
  drugProductBarcode: string;
  scannedUPCs: string[];  // Legacy: Keep for now or deprecate
  scans: PackagingScan[]; // NEW: Track detailed scans

  // Manual entry fields (Current/Last scanned)
  lotNumber: string;
  expiryDate: string;

  // Packaging details
  packagingType: 'bottle' | 'blister_pack' | 'compliance_pack' | 'special';
  blueBagNumber: string;

  // Special requirements
  temperatureControlled: boolean;
  childProofCap: boolean;
  lightSensitive: boolean;
  largeFont: boolean;
  easyOpenCap: boolean;

  // Notes
  technicianNotes: string;

  // Scan status
  drugProductScanned: boolean;
  lotExpiryCaptured: boolean;
  labelsGenerated: boolean;

  // Verification status
  verifiedDrugInfo: {
    name: string;
    din: string;
    manufacturer: string;
    upc?: string;
    strength?: string;
  } | null;

  // UPC mismatch tracking
  upcMismatch: {
    expected: string;
    scanned: string;
  } | null;

  // Step completion
  completedSteps: number[];
  activeStep: number;

  // Completion tracking
  isFullyVerified: boolean;
  verifiedAt?: string;
}

/**
 * Gets empty packaging verification object for a medication
 */
export const getEmptyPackagingVerification = (
  medication: PackagingMedicationData,
  hasNarcotics: boolean = false
): PackagingDrugVerification => ({
  // Medication info
  rxNumber: medication.rxNumber,
  medicationName: medication.medicationName,
  din: medication.din,
  upc: medication.upc,
  strength: medication.strength,
  quantity: medication.quantity,
  packCount: medication.packCount,

  // Barcode scans
  rxLabelBarcode: '',
  drugProductBarcode: '',
  scannedUPCs: [],
  scans: [],

  // Manual entry
  lotNumber: '',
  expiryDate: '',

  // Packaging details
  packagingType: 'bottle',
  blueBagNumber: '',

  // Special requirements
  temperatureControlled: false,
  childProofCap: hasNarcotics,
  lightSensitive: false,
  largeFont: false,
  easyOpenCap: false,

  // Notes
  technicianNotes: '',

  // Scan status
  drugProductScanned: false,
  lotExpiryCaptured: false,
  labelsGenerated: false,

  // Verification status
  verifiedDrugInfo: null,
  upcMismatch: null,

  // Step tracking
  completedSteps: [],
  activeStep: 0,

  // Completion
  isFullyVerified: false,
});

/**
 * Checks if packaging verification is complete for a drug
 * Simplified check - only require essential fields
 */
export const isPackagingVerificationComplete = (
  verification: PackagingDrugVerification
): boolean => {
  // Essential requirements only
  // const hasLotAndExpiry = Boolean(
  //   verification.lotNumber &&
  //   verification.lotNumber.length >= 3 &&
  //   verification.expiryDate &&
  //   verification.expiryDate.length > 0
  // );

  // Check if we have enough scans and each scan has lot/expiry
  const requiredScans = verification.packCount;
  const currentScans = verification.scans || [];
  
  const hasEnoughScans = currentScans.length >= requiredScans;
  const allScansValid = currentScans.every(s => 
    s.barcode && 
    s.lotNumber && s.lotNumber.length >= 3 && 
    s.expiryDate && s.expiryDate.length > 0
  );

  // Either marked as fully verified OR has all essential fields
  return Boolean(verification.isFullyVerified || (hasEnoughScans && allScansValid));
};
